<script setup lang="ts">
import { Bell } from '@lucide/vue';
import { computed, ref } from 'vue';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { useNotifications } from '../composables/useNotifications';
import { useNotificationsI18n } from '../composables/useNotificationsI18n';
import { usePortalWebPush } from '../composables/usePortalWebPush';

import NotificationList from './NotificationList.vue';

const { t } = useNotificationsI18n();

const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead, deleteNotification, refresh } =
    useNotifications();

const {
    supported: pushSupported,
    vapidServerStatus,
    permission: pushPermission,
    hasSubscription,
    busy: pushBusy,
    testPushBusy,
    lastError: pushLastError,
    canToggle,
    enablePush,
    disablePush,
    sendTestPush,
    trySyncExistingSubscription,
    refreshAvailability,
} = usePortalWebPush();

const pushErrorI18nCodes = new Set([
    'error_not_configured',
    'error_invalid_vapid_key',
    'error_push_service',
    'error_invalid_subscription',
    'error_sw_registration',
]);

const pushErrorLabel = computed(() => {
    const code = pushLastError.value;
    if (!code) {
        return '';
    }
    if (pushErrorI18nCodes.has(code)) {
        return t(`notifications.push.${code}`);
    }
    return code;
});

const isOpen = ref(false);

function handleOpenChange(open: boolean) {
    isOpen.value = open;
    if (open) {
        pushLastError.value = null;
        refresh();
        void refreshAvailability();
        void trySyncExistingSubscription();
    }
}

async function handleMarkRead(id: string) {
    await markAsRead(id);
}

async function handleMarkAllRead() {
    await markAllAsRead();
}

async function handleDelete(id: string) {
    await deleteNotification(id);
}

async function handleEnablePush() {
    await enablePush();
}

async function handleDisablePush() {
    await disablePush();
}

async function handleTestPush() {
    await sendTestPush();
}
</script>

<template>
    <DropdownMenu @update:open="handleOpenChange">
        <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon" class="relative">
                <Bell class="size-5" />
                <span
                    v-if="unreadCount > 0"
                    class="bg-destructive text-destructive-foreground absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full text-[10px] font-medium"
                >
                    {{ unreadCount > 9 ? '9+' : unreadCount }}
                </span>
            </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" class="w-80 p-0">
            <NotificationList
                :notifications="notifications"
                :is-loading="isLoading"
                @mark-read="handleMarkRead"
                @mark-all-read="handleMarkAllRead"
                @delete="handleDelete"
            />
            <div
                v-if="pushSupported"
                class="border-border text-muted-foreground space-y-2 border-t px-3 py-2 text-xs"
            >
                <p class="text-foreground font-medium">{{ t('notifications.push.browser_title') }}</p>
                <p v-if="pushErrorLabel" class="text-destructive">{{ pushErrorLabel }}</p>
                <p v-if="vapidServerStatus === 'missing'">{{ t('notifications.push.not_configured') }}</p>
                <p v-else-if="vapidServerStatus === 'error'">{{ t('notifications.push.check_failed') }}</p>
                <p v-else-if="pushPermission === 'denied'">{{ t('notifications.push.denied') }}</p>
                <template v-else-if="canToggle">
                    <p v-if="hasSubscription">{{ t('notifications.push.enabled_hint') }}</p>
                    <div v-if="hasSubscription" class="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            class="min-w-0 flex-1"
                            :disabled="pushBusy || testPushBusy"
                            @click="handleTestPush"
                        >
                            {{ t('notifications.push.test') }}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            class="min-w-0 flex-1"
                            :disabled="pushBusy || testPushBusy"
                            @click="handleDisablePush"
                        >
                            {{ t('notifications.push.disable') }}
                        </Button>
                    </div>
                    <Button
                        v-else
                        type="button"
                        variant="outline"
                        size="sm"
                        class="w-full"
                        :disabled="pushBusy"
                        @click="handleEnablePush"
                    >
                        {{ t('notifications.push.enable') }}
                    </Button>
                </template>
            </div>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
